import { analectById } from "./analects";
import { BUFFS, GATE_ORDER, GATES, TOTAL_REQUIREMENT, gateById } from "./gates";
import type { BuffId } from "./gates";
import { applyAnswer, getRecord } from "./progress";
import type { GateId, ProgressState, VirtueId } from "./types";

export const PLAYER_MAX_HP = 100;
const COUNTER_BASE = 13;
const SHIELD_CAP = 40;
const MANA_BASE = 4;
const MANA_CAP = 6;

/** Ephemeral per-quote duel state. Buff stacks are the real progress; reaching
 * each gate's requirement "breaks" it, and 誤解度 falls as stacks accrue. */
export interface Combat {
  analectId: number;
  playerHp: number; // 你的氣勢
  buffs: Record<BuffId, number>; // 明義/破誤/踐履/反思
  shield: number; // 護盾, absorbs the opponent's counter
  silenced: number; // rounds the opponent is silenced
  round: number;
  mana: number;
  maxMana: number;
  combo: number;
  bestCombo: number;
}

export interface CombatEvent {
  kind: "hit" | "miss" | "combo" | "shield" | "silence" | "counter" | "won" | "lost";
  gate?: GateId;
  amount?: number;
  buff?: BuffId;
}

export interface ResolveResult {
  combat: Combat;
  progress: ProgressState;
  events: CombatEvent[];
  outcome: "hit" | "miss" | "won" | "lost";
  damage: number;
  virtue?: VirtueId;
  questsJustDone?: boolean;
}

export function emptyBuffs(): Record<BuffId, number> {
  return { ming: 0, po: 0, jian: 0, fan: 0 };
}

export function comboMultiplier(combo: number): number {
  return 1 + Math.max(0, combo - 1) * 0.2;
}

function maxManaForRound(round: number): number {
  return Math.min(MANA_CAP, MANA_BASE + round - 1);
}

/** Gates already mastered-and-not-due start pre-broken (their buff pre-filled). */
export function createCombat(progress: ProgressState, id: number, now: number = Date.now()): Combat {
  const buffs = emptyBuffs();
  const record = getRecord(progress, id);
  for (const gate of GATES) {
    const srs = record.gates[gate.id];
    const solid = srs.reps >= 1 && !(srs.dueAt > 0 && srs.dueAt <= now);
    if (solid) buffs[gate.buff] = gate.requirement;
  }
  return {
    analectId: id,
    playerHp: PLAYER_MAX_HP,
    buffs,
    shield: 0,
    silenced: 0,
    round: 1,
    mana: MANA_BASE,
    maxMana: MANA_BASE,
    combo: 0,
    bestCombo: 0,
  };
}

// ---- derived selectors ----
export function gateStacks(combat: Combat, gate: GateId): number {
  return combat.buffs[gateById(gate).buff];
}
export function gateBroken(combat: Combat, gate: GateId): boolean {
  const def = gateById(gate);
  return combat.buffs[def.buff] >= def.requirement;
}
export function unmetStacks(combat: Combat): number {
  return GATES.reduce((sum, gate) => sum + Math.max(0, gate.requirement - combat.buffs[gate.buff]), 0);
}
/** 誤解度 0..100 — opponent resistance, derived from unmet buff requirements. */
export function misunderstanding(combat: Combat): number {
  return Math.round((100 * unmetStacks(combat)) / TOTAL_REQUIREMENT);
}
export function allBroken(combat: Combat): boolean {
  return unmetStacks(combat) === 0;
}
export function nextUnbrokenGate(combat: Combat): GateId {
  return GATE_ORDER.find((gate) => !gateBroken(combat, gate)) ?? "meaning";
}
export function buffTotals(combat: Combat) {
  return BUFFS.map((buff) => ({
    ...buff,
    stacks: combat.buffs[buff.id],
    requirement: gateById(buff.gate).requirement,
  }));
}

// ---- mana / rounds ----
export function spendMana(combat: Combat, cost: number): Combat {
  if (combat.mana >= cost) return { ...combat, mana: combat.mana - cost };
  // soft: never blocks — draw breath (new round), refill, then spend
  const round = combat.round + 1;
  const maxMana = maxManaForRound(round);
  return { ...combat, round, maxMana, mana: Math.max(0, maxMana - cost) };
}
export function endTurn(combat: Combat): Combat {
  const round = combat.round + 1;
  const maxMana = maxManaForRound(round);
  const silenced = Math.max(0, combat.silenced - 1);
  return { ...combat, round, maxMana, mana: maxMana, silenced };
}

// ---- answer resolution ----
export function resolveAnswer(
  combat: Combat,
  progress: ProgressState,
  gate: GateId,
  correct: boolean,
  now: number = Date.now(),
): ResolveResult {
  const analect = analectById.get(combat.analectId);
  const def = gateById(gate);
  const before = misunderstanding(combat);
  const answered = analect ? applyAnswer(progress, analect, gate, correct, now) : null;
  const nextProgress = answered ? answered.state : progress;
  const events: CombatEvent[] = [];
  const next: Combat = { ...combat, buffs: { ...combat.buffs } };

  if (correct) {
    const combo = combat.combo + 1;
    next.combo = combo;
    next.bestCombo = Math.max(combat.bestCombo, combo);

    let grant = 1;
    if (combo >= 2) grant += 1; // 連對
    if (gate === "misread" && before > 60) grant += 1; // 誤解度高，駁論更狠
    next.buffs[def.buff] = Math.min(def.requirement, next.buffs[def.buff] + grant);

    if (gate === "context") {
      next.shield = Math.min(SHIELD_CAP, next.shield + 16);
      events.push({ kind: "shield", amount: 16 });
    }
    if (gate === "recall" && before < 40) {
      next.silenced += 1;
      events.push({ kind: "silence" });
    }

    const damage = Math.round(def.damage * comboMultiplier(combo));
    events.unshift({ kind: combo >= 2 ? "combo" : "hit", gate, amount: damage, buff: def.buff });

    const outcome = allBroken(next) ? "won" : "hit";
    if (outcome === "won") events.push({ kind: "won", gate });
    return {
      combat: next,
      progress: nextProgress,
      events,
      outcome,
      damage,
      virtue: answered?.virtue,
      questsJustDone: answered?.questsJustDone,
    };
  }

  // wrong answer → opponent counters your 氣勢
  next.combo = 0;
  let counter = COUNTER_BASE + Math.floor(before / 20);
  if (next.silenced > 0) {
    next.silenced -= 1;
    counter = 0;
    events.push({ kind: "silence" });
  } else if (next.shield > 0) {
    const absorbed = Math.min(next.shield, counter);
    next.shield -= absorbed;
    counter -= absorbed;
    events.push({ kind: "shield", amount: absorbed });
  }
  if (counter > 0) {
    next.playerHp = Math.max(0, next.playerHp - counter);
    events.push({ kind: "counter", gate, amount: counter });
  }
  events.unshift({ kind: "miss", gate });

  const outcome = next.playerHp <= 0 ? "lost" : "miss";
  if (outcome === "lost") events.push({ kind: "lost" });
  return {
    combat: next,
    progress: nextProgress,
    events,
    outcome,
    damage: counter,
    virtue: answered?.virtue,
    questsJustDone: answered?.questsJustDone,
  };
}
